/**
 * お問い合わせ - フォーム（page-contact-form.php／Contact Form 7）
 * - 個人情報同意チェックによる「確認画面へ進む」ボタンの有効化
 * - 郵便番号からの住所自動入力（zipcloud API）
 * - ご職業セレクトの未選択状態（is-emptyクラス）の同期
 * - 「確認画面へ進む」前の簡易バリデーション（必須項目・メール一致・墓所種類）
 * - 確認画面への実入力値の反映
 * - 入力画面・確認画面の表示切り替え
 * - 実際の送信（CF7への実送信）とCF7イベントに応じた画面遷移
 *
 * 入力パネル（.js-contact-form-input-panel）・確認パネル（.js-contact-form-confirm-panel）は
 * どちらも同じ1つのCF7フォーム（<form class="wpcf7-form">）の中に含まれている構成
 * （contact-backup/contact-cf7.php参照）。
 */

function getCf7Form() {
  const inputPanel = document.querySelector('.js-contact-form-input-panel');
  return inputPanel ? inputPanel.closest('form.wpcf7-form') : document.querySelector('form.wpcf7-form');
}

function initAgreeCheckbox() {
  const agreeCheckbox = document.querySelector('.js-agree-checkbox');
  const reviewButton = document.querySelector('.js-review-button');

  if (!agreeCheckbox || !reviewButton) {
    return;
  }

  const syncReviewButtonState = () => {
    reviewButton.disabled = !agreeCheckbox.checked;
  };

  agreeCheckbox.addEventListener('change', syncReviewButtonState);
  syncReviewButtonState();
}

function initOccupationSelectEmptyState() {
  const occupationSelect = document.getElementById('cf-occupation');

  if (!occupationSelect) {
    return;
  }

  const syncEmptyState = () => {
    occupationSelect.classList.toggle('is-empty', occupationSelect.value === '');
  };

  occupationSelect.addEventListener('change', syncEmptyState);
  syncEmptyState();
}

function initZipAutofill() {
  const zipButtons = document.querySelectorAll('.js-zip-autofill');

  zipButtons.forEach((button) => {
    let activeRequest;

    button.addEventListener('click', async () => {
      const zipInput = document.getElementById(button.dataset.zipTarget || '');
      const addressInput = document.getElementById(button.dataset.addressTarget || '');
      const statusElement = document.getElementById(button.dataset.statusTarget || '');

      if (!zipInput || !addressInput) {
        return;
      }

      const zipCode = zipInput.value
        .replace(/[０-９]/g, (number) => String.fromCharCode(number.charCodeAt(0) - 0xfee0))
        .replace(/[^0-9]/g, '');

      if (zipCode.length !== 7) {
        if (statusElement) {
          statusElement.textContent = '郵便番号を7桁で入力してください。';
        }
        zipInput.focus();
        return;
      }

      activeRequest?.abort();
      const requestController = new AbortController();
      activeRequest = requestController;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      if (statusElement) {
        statusElement.textContent = '住所を検索しています。';
      }

      try {
        const params = new URLSearchParams({
          zipcode: zipCode,
          limit: '1',
        });
        const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?${params}`, {
          signal: requestController.signal,
        });

        if (!response.ok) {
          throw new Error('Postal code request failed');
        }

        const data = await response.json();
        const result = data && data.results && data.results[0];

        if (data.status !== 200 || !result) {
          if (statusElement) {
            statusElement.textContent = '該当する住所が見つかりませんでした。';
          }
          return;
        }

        addressInput.value = `${result.address1}${result.address2}${result.address3}`;
        addressInput.dispatchEvent(new Event('input', { bubbles: true }));
        addressInput.dispatchEvent(new Event('change', { bubbles: true }));

        if (statusElement) {
          statusElement.textContent = '住所を自動入力しました。番地以降を入力してください。';
        }

        addressInput.focus();
      } catch (error) {
        if (error.name !== 'AbortError' && statusElement) {
          statusElement.textContent = '住所を取得できませんでした。時間をおいて再度お試しください。';
        }
      } finally {
        if (!requestController.signal.aborted) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          activeRequest = undefined;
        }
      }
    });
  });
}

/**
 * CF7が[text*]等のタグに自動付与する<span class="wpcf7-form-control-wrap" data-name="xxx">
 * に対して、CF7標準のエラー表示（.wpcf7-not-valid / .wpcf7-not-valid-tip）を疑似的に追加する。
 */
function markInvalid(cf7Form, fieldName, message) {
  const wrap = cf7Form.querySelector(`.wpcf7-form-control-wrap[data-name="${fieldName}"]`);

  if (!wrap) {
    return;
  }

  wrap.querySelector('.wpcf7-form-control')?.classList.add('wpcf7-not-valid');

  let tip = wrap.querySelector('.wpcf7-not-valid-tip');
  if (!tip) {
    tip = document.createElement('span');
    tip.className = 'wpcf7-not-valid-tip';
    tip.setAttribute('role', 'alert');
    wrap.appendChild(tip);
  }
  tip.textContent = message;
}

function clearInvalid(cf7Form, fieldName) {
  const wrap = cf7Form.querySelector(`.wpcf7-form-control-wrap[data-name="${fieldName}"]`);

  if (!wrap) {
    return;
  }

  wrap.querySelector('.wpcf7-form-control')?.classList.remove('wpcf7-not-valid');
  wrap.querySelector('.wpcf7-not-valid-tip')?.remove();
}

/**
 * 生HTML（CF7タグではない）のフィールド用の簡易エラー表示。
 * 対象要素の直後に.wpcf7-not-valid-tip相当のメッセージを挿入する。
 */
function markInvalidRaw(container, message) {
  container.classList.add('wpcf7-not-valid');

  let tip = container.querySelector(':scope > .wpcf7-not-valid-tip');
  if (!tip) {
    tip = document.createElement('span');
    tip.className = 'wpcf7-not-valid-tip';
    tip.setAttribute('role', 'alert');
    container.appendChild(tip);
  }
  tip.textContent = message;
}

function clearInvalidRaw(container) {
  container.classList.remove('wpcf7-not-valid');
  container.querySelector(':scope > .wpcf7-not-valid-tip')?.remove();
}

/**
 * 「確認画面へ進む」前の簡易バリデーション。
 * CF7側の必須タグ（[text*]等）は実送信時にサーバー側でも検証されるが、
 * ここでは確認画面へ進める前にクライアント側で先回りしてチェックする。
 * 戻り値: 検証OKならtrue、NGならfalse（同時にエラー表示も行う）。
 */
function validateBeforeReview(cf7Form) {
  let isValid = true;

  const requiredTextFields = [
    ['your-name', 'cf-name', 'お名前を入力してください。'],
    ['name-kana', 'cf-name-kana', 'フリガナを入力してください。'],
    ['tel', 'cf-tel', '電話番号を入力してください。'],
    ['email', 'cf-email', 'メールアドレスを入力してください。'],
    ['email-confirm', 'cf-email-confirm', 'メールアドレス（確認）を入力してください。'],
    ['zip', 'cf-zip', '郵便番号を入力してください。'],
    ['address', 'cf-address', 'ご住所を入力してください。'],
  ];

  requiredTextFields.forEach(([fieldName, elementId, message]) => {
    const field = document.getElementById(elementId);
    if (field && field.value.trim() === '') {
      markInvalid(cf7Form, fieldName, message);
      isValid = false;
    } else {
      clearInvalid(cf7Form, fieldName);
    }
  });

  const emailField = document.getElementById('cf-email');
  const emailConfirmField = document.getElementById('cf-email-confirm');
  if (
    emailField &&
    emailConfirmField &&
    emailField.value.trim() !== '' &&
    emailConfirmField.value.trim() !== '' &&
    emailField.value.trim() !== emailConfirmField.value.trim()
  ) {
    markInvalid(cf7Form, 'email-confirm', 'メールアドレスが一致しません。');
    isValid = false;
  }

  const graveTypeGroup = cf7Form.querySelector('.contact-form__checkbox-group');
  const graveTypeChecked = cf7Form.querySelectorAll('input[name="grave_type[]"]:checked');
  if (graveTypeGroup) {
    if (graveTypeChecked.length === 0) {
      markInvalidRaw(graveTypeGroup, 'ご希望の墓所の種類を1つ以上選択してください。');
      isValid = false;
    } else {
      clearInvalidRaw(graveTypeGroup);
    }
  }

  return isValid;
}

/**
 * 実際の入力値を確認画面の[data-confirm-value]要素に反映する。
 */
function populateConfirmPanel(cf7Form) {
  const setValue = (key, value, fallback) => {
    const target = cf7Form.querySelector(`[data-confirm-value="${key}"]`);
    if (target) {
      target.textContent = value && value.trim() !== '' ? value.trim() : fallback;
    }
  };

  const getFieldValue = (id) => document.getElementById(id)?.value ?? '';

  setValue('name', getFieldValue('cf-name'), '未入力');
  setValue('name-kana', getFieldValue('cf-name-kana'), '未入力');
  setValue('birthdate', getFieldValue('cf-birthdate'), '未入力');
  setValue('tel', getFieldValue('cf-tel'), '未入力');
  setValue('email', getFieldValue('cf-email'), '未入力');
  setValue('zip', getFieldValue('cf-zip'), '未入力');

  const address = getFieldValue('cf-address');
  const building = getFieldValue('cf-building');
  setValue('address', building.trim() !== '' ? `${address} ${building}` : address, '未入力');

  const checkedGender = cf7Form.querySelector('input[name="gender"]:checked');
  const genderLabel = checkedGender?.closest('.contact-form__radio')?.querySelector('span:last-child')?.textContent;
  setValue('gender', genderLabel ?? '', '未回答');

  const occupationSelect = document.getElementById('cf-occupation');
  const occupationLabel = occupationSelect?.selectedOptions?.[0]?.textContent;
  const occupationValue = occupationSelect?.value ?? '';
  setValue('occupation', occupationValue !== '' ? occupationLabel : '', '未選択');

  const checkedGraveTypes = Array.from(cf7Form.querySelectorAll('input[name="grave_type[]"]:checked'));
  const graveTypeLabels = checkedGraveTypes
    .map((checkbox) => checkbox.closest('.contact-form__checkbox')?.querySelector('span:last-child')?.textContent)
    .filter(Boolean);
  setValue('grave-type', graveTypeLabels.join('、'), '未選択');

  setValue('message', getFieldValue('cf-message'), '特になし');
}

function initConfirmToggle() {
  const cf7Form = getCf7Form();
  const inputPanel = document.querySelector('.js-contact-form-input-panel');
  const confirmPanel = document.querySelector('.js-contact-form-confirm-panel');
  const reviewButton = document.querySelector('.js-review-button');
  const backButton = document.querySelector('.js-confirm-back-button');
  const sendButton = document.querySelector('.js-confirm-send-button');

  if (!cf7Form || !inputPanel || !confirmPanel || !reviewButton || !backButton || !sendButton) {
    return;
  }

  const scrollToPanelTop = (panel) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panel.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const showInputPanel = () => {
    confirmPanel.hidden = true;
    inputPanel.hidden = false;
    scrollToPanelTop(inputPanel);
  };

  const showConfirmPanel = () => {
    inputPanel.hidden = true;
    confirmPanel.hidden = false;
    scrollToPanelTop(confirmPanel);
  };

  const resetSendButton = () => {
    sendButton.disabled = false;
    sendButton.removeAttribute('aria-busy');
  };

  reviewButton.addEventListener('click', (event) => {
    event.preventDefault();

    if (!validateBeforeReview(cf7Form)) {
      return;
    }

    populateConfirmPanel(cf7Form);
    showConfirmPanel();
  });

  backButton.addEventListener('click', (event) => {
    event.preventDefault();
    showInputPanel();
  });

  // 実際の送信は、確認画面内に隠して配置しているCF7純正の送信ボタン
  // （[submit class:contact-form__submit-proxy]、CSS上display:noneで視覚的には非表示）を
  // クリックすることで行う。見た目上の「送信する」ボタン自体はtype="button"のプレーン
  // ボタンで、CF7自身の送信フローとは独立している。
  sendButton.addEventListener('click', () => {
    const proxySubmit = cf7Form.querySelector('.contact-form__submit-proxy');
    if (!proxySubmit) {
      return;
    }
    sendButton.disabled = true;
    sendButton.setAttribute('aria-busy', 'true');
    proxySubmit.click();
  });

  // CF7から送信エラー系のイベントが来た場合は入力画面へ戻し、送信ボタンの状態も戻す。
  ['wpcf7invalid', 'wpcf7mailfailed', 'wpcf7spam', 'wpcf7aborted'].forEach((eventName) => {
    cf7Form.addEventListener(eventName, () => {
      resetSendButton();
      showInputPanel();
    });
  });

  // 送信成功時は、サンクスページ（data-thankyou-url）へリダイレクトする。
  cf7Form.addEventListener('wpcf7mailsent', () => {
    const thankyouUrl = document.getElementById('main-content')?.dataset.thankyouUrl;
    if (thankyouUrl) {
      window.location.href = thankyouUrl;
    } else {
      resetSendButton();
    }
  });
}

initAgreeCheckbox();
initOccupationSelectEmptyState();
initZipAutofill();
initConfirmToggle();
